import { getLeadCategories, getLeadCategoryCounts, matchesLeadCategory } from "../utils/leadCategories";
import { makeLeadFavoriteKey } from "../services/LeadsFavoritesService";
import type { Lead, UpdateLeadRequest } from "../services/LeadsService";
import { buildRetailerLookupUrl, getRetailerLinkLabel } from "../utils/productUrls";

export interface LeadEditFormState {
  upc: string;
  title: string;
  sku: string;
  category: string;
  lowestPrice: number;
  msrp: number;
  onlinePrice: number;
  image: string;
}

export const getAvailableCategories = (
  _leads?: Lead[],
  manualCategories?: string[],
  hiddenCategories?: string[]
): string[] => getLeadCategories(manualCategories, hiddenCategories);

export const getCategoryCounts = getLeadCategoryCounts;

export const countSavedLeads = ({
  leads,
  savedLeadIds,
  retailer,
}: {
  leads?: Lead[];
  savedLeadIds?: string[];
  retailer: string;
}): number => {
  if (!leads?.length || !savedLeadIds?.length) return 0;
  const savedSet = new Set(savedLeadIds);
  return leads.filter((lead) => savedSet.has(makeLeadFavoriteKey(retailer, lead.id))).length;
};

const matchesQuery = (lead: Lead, query: string): boolean => {
  const q = query.toLowerCase();
  return [lead.title, lead.sku, lead.id, lead.brand].some(value => (value || "").toLowerCase().includes(q));
};

export const filterLeads = ({
  leads,
  searchQuery,
  selectedCategory,
  selectedCategories,
  showSavedOnly,
  savedLeadIds,
  retailer,
}: {
  leads?: Lead[];
  searchQuery: string;
  /** Single category key; "all" or omitted means no filter. */
  selectedCategory?: string;
  /** Multi-select keys; empty or omitted means no filter. Takes precedence over selectedCategory. */
  selectedCategories?: string[];
  showSavedOnly: boolean;
  savedLeadIds?: string[];
  retailer: string;
}): Lead[] => {
  if (!leads?.length) return [];
  const trimmedQuery = searchQuery.trim();
  const savedSet = new Set(savedLeadIds ?? []);
  const categoryKeys = selectedCategories?.length
    ? selectedCategories
    : selectedCategory && selectedCategory !== "all"
      ? [selectedCategory]
      : [];
  return leads.filter((lead) => {
    if (trimmedQuery && !matchesQuery(lead, trimmedQuery)) return false;
    if (categoryKeys.length > 0 && !categoryKeys.some((key) => matchesLeadCategory(lead, key))) return false;
    if (showSavedOnly && !savedSet.has(makeLeadFavoriteKey(retailer, lead.id))) return false;
    return true;
  });
};

export const formatPrice = (price: number): string =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(price);

export type TranslateFn = (key: string, params?: Record<string, string | number>) => string;

export const formatRelativeTime = (timestamp: number, t: TranslateFn, now: number = Date.now()): string => {
  const diffMs = now - timestamp;
  const mins = Math.floor(diffMs / 60000);
  const hrs = Math.floor(mins / 60);
  const days = Math.floor(hrs / 24);
  if (days > 0) return t("leads.relDaysAgo", { count: days });
  if (hrs > 0) return t("leads.relHoursAgo", { count: hrs });
  if (mins > 0) return t("leads.relMinutesAgo", { count: mins });
  return t("leads.relJustNow");
};

export const getLeadOriginalPrice = (msrp: number, onlinePrice: number): number =>
  Math.max(msrp || 0, onlinePrice || 0);

export const getLeadDiscountPercent = (originalPrice: number, lowestPrice: number): number =>
  originalPrice > lowestPrice ? Math.round(((originalPrice - lowestPrice) / originalPrice) * 100) : 0;

export const resolveWalmartIds = (
  lead: Pick<Lead, "id" | "sku">,
  retailer: string
): { upc: string; sku: string } => {
  if ((retailer !== "walmart" && retailer !== "walmart_online") || !lead.sku || !lead.id || lead.sku === lead.id)
    return { upc: lead.id, sku: lead.sku || lead.id };
  const longer = lead.id.length >= lead.sku.length ? { upc: lead.id, sku: lead.sku } : { upc: lead.sku, sku: lead.id };
  return longer;
};

export const getLeadStockSearchSku = (
  lead: Pick<Lead, "id" | "sku">,
  retailer: string
): string => {
  const resolved = resolveWalmartIds(lead, retailer);
  if (retailer === "target" || retailer === "target_online") {
    return resolved.upc;
  }
  return resolved.sku || resolved.upc;
};

export const getLeadExternalLink = (
  retailer: string,
  lead: Pick<Lead, "id" | "sku">
): { url: string; label: string } => {
  const id = (lead.id || lead.sku || "").trim();
  const sku = (lead.sku || lead.id || "").trim();
  switch (retailer) {
    case "target":
    case "target_online":
      return { url: buildRetailerLookupUrl(retailer, id), label: "Target.com" };
    case "home_depot":
      return { url: buildRetailerLookupUrl(retailer, id), label: "HD.com" };
    case "dollar_general":
      return { url: buildRetailerLookupUrl(retailer, id), label: "DollarGeneral.com" };
    case "bestbuy":
      return { url: buildRetailerLookupUrl(retailer, sku), label: "BestBuy.com" };
    case "lowes":
      return { url: buildRetailerLookupUrl(retailer, sku), label: "Lowes.com" };
    case "tractor_supply":
      return { url: buildRetailerLookupUrl(retailer, sku), label: "TractorSupply.com" };
    case "northern_tool":
      return { url: buildRetailerLookupUrl(retailer, sku), label: "NorthernTool.com" };
    case "samsclub":
      return { url: buildRetailerLookupUrl(retailer, id), label: "SamsClub.com" };
    case "ace_hardware":
      return { url: buildRetailerLookupUrl(retailer, sku), label: "AceHardware.com" };
    case "costco":
      return { url: buildRetailerLookupUrl(retailer, sku), label: "Costco.com" };
    case "amazon":
      return { url: buildRetailerLookupUrl(retailer, sku), label: "Amazon.com" };
    case "walmart":
    case "walmart_online":
    default:
      return { url: buildRetailerLookupUrl(retailer, id), label: getRetailerLinkLabel(retailer) };
  }
};

export const buildUpdateLeadRequest = (form: LeadEditFormState): UpdateLeadRequest => ({
  upc: form.upc,
  title: form.title,
  sku: form.sku,
  category: form.category,
  lowestPrice: Number(form.lowestPrice),
  msrp: Number(form.msrp),
  onlinePrice: Number(form.onlinePrice),
  image: form.image,
});
